/*
  bf_defaultpick.ts
  Copyright (c) 2013-present Human Cube Inc.

  Generates a default pick (zone or capital) for a player.

  Must all be synchronous and not involve any external DB, file access or external user data.
  These restrictions are to allow it to be re-used in other environments such as the client.
*/

import BF from './bfconst1';
import bfH from './bf_helper';
import { Game, PlayerPickTurn } from './gameTypes';


export function generatePickZone (game: Game, playerIndex: number): PlayerPickTurn {
  // Returns a generated turn dictionary.
  const value = [0];
  for(let j = 1; j <= game.zones; j++) {
    if( game.zoneOwner[j] > 0 || !bfH.isLand(game, j)) {
      value[j] = 0;  // Not a valid potential pick. 
      continue;
    }

    value[j] = 1000000;  // Base value for possible pick. 
    value[j] += game.zoneIncome[j] * 10;
    if( bfH.isIsland(game, j) ) {
      value[j] += 10;   // Small boost for being an island.
    }

    game.bordersLand[j].forEach((zoneID) => {
      const zoneOwner = game.zoneOwner[zoneID];
      if(zoneOwner >= 0) {
        if(zoneOwner === playerIndex) {
          value[j] += 5;  // Little boost for owning a neighbour.
        }
        else {
          if( !(game.fixedAlly[zoneOwner] & (1 << playerIndex)) ) {
            // Not owned by an ally.
            value[j] -= 2;  // Little penalty for having an unfriendly neighbour.
          }
        }
      }
    });
  }

  // Assume that player 0's forces are the starting forces.
  if( Array.isArray(game.force) && Array.isArray(game.force[0]) ) {
    for(let i = 0; i < game.force[0].length; i++) {
      const zoneID = game.force[0][i][BF.FORCE_INDEX_ZONE];

      let creditZoneID = zoneID;
      if( bfH.isWater(game, zoneID) ) {
        // Credit water forces to the land zones who have the navyZone for the given zone:
        if(game.navyZone && Array.isArray(game.navyZone)) {
          const index = game.navyZone.indexOf(zoneID);
          if(index > 0) {
            creditZoneID = index;
          }
          else {
            creditZoneID = -1;
          }
        }
      }

      if(value[creditZoneID] === 0) {
        continue;  // Not a potential zone pick.
      } 

      const forceType = game.force[0][i][BF.FORCE_INDEX_TYPE];
      const cost = game.forceCost[forceType];

      if( cost && creditZoneID > 0 && creditZoneID <= game.zones ) {
        value[creditZoneID] += cost;
        if( !(game.flags & BF.GAME_FLAG_FACTORY_AT_CAPITAL) ) {
          // If no automatic factory at capital, then boost up the value
          // of existing production places.
          if( game.forceProduction[forceType] > 0 ) {
            value[creditZoneID] += game.forceProduction[forceType] * 1000000;
          }
        }
      }
    }
  }

  let best = -1000000;  // not a good pick
  let bestID = 0;
  for(let j = 1; j <= game.zones; j++) {
    const bestTry = value[j];
    if( bestTry > 0) {
      if( bestTry >= best ) {
        best = bestTry;
        bestID = j;
      }
      else if(bestTry === best && Math.random() > 0.5) {
        // Random chance to select an equal weighted one.
        best = bestTry;
        bestID = j;
      }
    }
  }
  return {pick: bestID};
}

export function generatePickCapital (game: Game, playerIndex: number): PlayerPickTurn {
  // Returns a generated turn dictionary for a capital pick turn.
  const value = [0];
  for(let j = 1; j <= game.zones; j++) {
    if( game.zoneOwner[j] !== playerIndex || !bfH.isLand(game, j) ) {
      value[j] = 0;
      continue;  // Not a potential selection.
    }

    value[j] = 1000000;  // Base value for being owned and being land.
    value[j] += game.zoneIncome[j] * 10;
    if(bfH.isIsland(game, j)) {
      value[j] += 100;   // Good bonus for being an island.
    }
    game.bordersLand[j].forEach((zoneID) => {
      const zoneOwner = game.zoneOwner[zoneID];
      if(zoneOwner >= 0) {
        if(zoneOwner === playerIndex) {
          value[j] += 10;  // Little boost for owning a neighbour.
        }
        else {
          if( !(game.fixedAlly[zoneOwner] & (1 << playerIndex)) ) {
            // Not owned by an ally.
            value[j] -= 10;  // Little penalty for having an unfriendly neighbour.
          }
        }
      }
    });
  }

  if( Array.isArray(game.force) && Array.isArray(game.force[playerIndex]) ) {
    for(var i = 0; i < game.force[playerIndex].length; i++) {
      const zoneID = game.force[playerIndex][i][BF.FORCE_INDEX_ZONE];
      if(value[zoneID] === 0) {
        continue; // Not a potential zone selection.
      } 
      const forceType = game.force[playerIndex][i][BF.FORCE_INDEX_TYPE];
      const cost = game.forceCost[forceType];
      if( cost && zoneID > 0 && zoneID <= game.zones ) {
        value[zoneID] += cost;
        if( !(game.flags & BF.GAME_FLAG_FACTORY_AT_CAPITAL) ) {
          // If no automatic factory at capital, then super boost up the value of existing
          // production places:
          if( game.forceProduction[forceType] > 0 ) {
            value[zoneID] += game.forceProduction[forceType] * 1000000;
          }
        }
      }
    }
  }

  // No boost for navyZone starting forces as they will not nessarily affect capital defence.

  let best = -1000000;  // not a good pick
  let bestID = 0;
  for(let j = 1; j <= game.zones; j++) {
    const bestTry = value[j];
    if( bestTry > 0 && bestTry >= best ) {
      best = bestTry;
      bestID = j;
    }
  }
  return {pick: bestID};
}
